import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { fetchServices, fetchDailyAffirmation } from "../lib/api";

const HERO_IMAGE =
  "https://images.pexels.com/photos/6707631/pexels-photo-6707631.jpeg";
const WELLNESS_IMAGE =
  "https://images.unsplash.com/photo-1444312645910-ffa973656eba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const STYLING_IMAGE =
  "https://images.pexels.com/photos/11311199/pexels-photo-11311199.jpeg";
const SOFT_IMAGE =
  "https://images.unsplash.com/photo-1521194263619-39ecc5b55c61?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const [services, setServices] = useState([]);
  const [daily, setDaily] = useState(null);

  useEffect(() => {
    fetchServices().then(setServices).catch(() => {});
    fetchDailyAffirmation().then(setDaily).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="pt-24 md:pt-28">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative px-6 md:px-12 lg:px-20 pt-12 md:pt-16 pb-24 md:pb-32"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 fade-up">
            <p
              className="font-eyebrow text-[11px] text-[#5C5C59] mb-8"
              data-testid="hero-eyebrow"
            >
              Wholistic Wellness · Est. {new Date().getFullYear()}
            </p>
            <h1
              data-testid="hero-headline"
              className="font-serif-display text-[56px] sm:text-[80px] lg:text-[120px] xl:text-[140px] text-[#1A1A1A]"
            >
              Return
              <br />
              <em className="editorial-italic text-[#7B8871]">softly</em>
              <br />
              to yourself.
            </h1>
            <div className="mt-10 max-w-xl">
              <p className="text-[#5C5C59] text-base md:text-lg leading-relaxed">
                Finka is a quiet practice for the woman who is ready to slow
                down. Somatic support, holistic coaching, bodywork, and the
                psychology of style, woven into one embodied conversation.
              </p>
              <div className="mt-10 flex items-center gap-4 flex-wrap">
                <Link
                  to="/booking"
                  data-testid="hero-cta-book"
                  className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F7F5F2] px-8 py-4 font-eyebrow text-[11px] hover:bg-[#7B8871] transition-colors duration-500"
                >
                  Book a session
                  <ArrowUpRight
                    strokeWidth={1.5}
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
                <Link
                  to="/affirmations"
                  data-testid="hero-cta-affirmations"
                  className="group inline-flex items-center gap-3 border border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 font-eyebrow text-[11px] hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-500"
                >
                  Read affirmations
                  <ArrowRight
                    strokeWidth={1.5}
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden">
              <img
                src={HERO_IMAGE}
                alt="A serene minimalist interior with floral arrangement"
                className="w-full h-[420px] md:h-[560px] object-cover grayscale-[10%] transition-transform duration-[1200ms] hover:scale-[1.03]"
                data-testid="hero-image"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-[#F7F5F2]/85 backdrop-blur-md p-5 border border-[#D8D4CC]">
                <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                  Today's affirmation
                </p>
                <p
                  data-testid="hero-daily-affirmation"
                  className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mt-3 leading-tight"
                >
                  <em className="editorial-italic">
                    {daily?.text || "I am rooted in the present, and the present is enough."}
                  </em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="editorial-rule mx-6 md:mx-12 lg:mx-20" />

      {/* INTRO / MANIFESTO */}
      <section
        data-testid="manifesto-section"
        className="px-6 md:px-12 lg:px-20 py-24 md:py-32"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <p className="font-eyebrow text-[11px] text-[#5C5C59]">
              The Practice
            </p>
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl text-[#1A1A1A] max-w-4xl">
              A modern sanctuary for the{" "}
              <em className="editorial-italic text-[#7B8871]">
                nervous system,
              </em>{" "}
              the body, and the way you dress your becoming.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10 max-w-4xl">
              <div>
                <p className="font-eyebrow text-[10px] text-[#5C5C59] mb-3">
                  01 / Regulate
                </p>
                <p className="text-[#1A1A1A] text-sm leading-relaxed">
                  Somatic, breath, and nervous system support to soften what's
                  been holding on.
                </p>
              </div>
              <div>
                <p className="font-eyebrow text-[10px] text-[#5C5C59] mb-3">
                  02 / Reconnect
                </p>
                <p className="text-[#1A1A1A] text-sm leading-relaxed">
                  Bodywork, movement, and intuitive coaching to return you to
                  yourself.
                </p>
              </div>
              <div>
                <p className="font-eyebrow text-[10px] text-[#5C5C59] mb-3">
                  03 / Reveal
                </p>
                <p className="text-[#1A1A1A] text-sm leading-relaxed">
                  Style as a mirror. Fashion psychology that lets your inside
                  show.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="editorial-rule mx-6 md:mx-12 lg:mx-20" />

      {/* SERVICES */}
      <section
        data-testid="services-section"
        className="px-6 md:px-12 lg:px-20 py-24 md:py-32"
      >
        <div className="flex items-baseline justify-between mb-16 md:mb-20 flex-wrap gap-6">
          <div>
            <p className="font-eyebrow text-[11px] text-[#5C5C59] mb-4">
              Services / Index
            </p>
            <h2 className="font-serif-display text-5xl md:text-7xl text-[#1A1A1A]">
              What we{" "}
              <em className="editorial-italic text-[#7B8871]">offer.</em>
            </h2>
          </div>
          <Link
            to="/booking"
            data-testid="services-cta-book"
            className="group inline-flex items-center gap-3 font-eyebrow text-[11px] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1"
          >
            Book a session
            <ArrowUpRight
              strokeWidth={1.5}
              className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          {/* Featured large card */}
          <div className="md:col-span-7 md:row-span-2 group relative overflow-hidden">
            <img
              src={WELLNESS_IMAGE}
              alt="Stacked stones representing balance"
              className="w-full h-[380px] md:h-[560px] object-cover transition-transform duration-[1500ms] group-hover:scale-105"
            />
            <div className="mt-6">
              <p className="font-eyebrow text-[10px] text-[#5C5C59]">01</p>
              <h3 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] mt-2">
                Wholistic Wellness Coaching
              </h3>
              <p className="text-[#5C5C59] text-sm leading-relaxed mt-3 max-w-md">
                Whole-self coaching that honors your mind, body, and inner
                world. Together, we build grounded practices around what your
                life actually needs, not what it's supposed to look like.
              </p>
              <Link
                to="/booking?service=holistic-coaching"
                data-testid="service-book-holistic-coaching"
                className="inline-flex items-center gap-2 mt-5 font-eyebrow text-[10px] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1 group/link"
              >
                Book
                <ArrowRight
                  strokeWidth={1.5}
                  className="w-3.5 h-3.5 transition-transform duration-300 group-hover/link:translate-x-1"
                />
              </Link>
            </div>
          </div>

          {services
            .filter((s) => s.id !== "holistic-coaching")
            .slice(0, 6)
            .map((s, idx) => (
              <div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className={`group ${
                  idx % 3 === 0
                    ? "md:col-span-5"
                    : idx % 2 === 0
                    ? "md:col-span-3"
                    : "md:col-span-2"
                } md:col-span-5`}
              >
                <div className="border-t border-[#D8D4CC] pt-6">
                  <div className="flex items-baseline justify-between">
                    <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                      0{idx + 2}
                    </p>
                    <p className="font-eyebrow text-[10px] text-[#7B8871]">
                      {s.duration}
                    </p>
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mt-3 leading-tight">
                    {s.name}
                  </h3>
                  <p className="text-[#5C5C59] text-sm leading-relaxed mt-3">
                    {s.description}
                  </p>
                  <Link
                    to={`/booking?service=${s.id}`}
                    data-testid={`service-book-${s.id}`}
                    className="inline-flex items-center gap-2 mt-5 font-eyebrow text-[10px] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1 group/link"
                  >
                    Book
                    <ArrowRight
                      strokeWidth={1.5}
                      className="w-3.5 h-3.5 transition-transform duration-300 group-hover/link:translate-x-1"
                    />
                  </Link>
                </div>
              </div>
            ))}
        </div>
      </section>

      <hr className="editorial-rule mx-6 md:mx-12 lg:mx-20" />

      {/* AFFIRMATIONS TEASER */}
      <section
        data-testid="affirmations-teaser"
        className="relative px-6 md:px-12 lg:px-20 py-32 md:py-40 overflow-hidden"
      >
        <img
          src={SOFT_IMAGE}
          alt="Soft geometric light and shadow on concrete"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-[#F7F5F2]/55" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <p className="font-eyebrow text-[11px] text-[#5C5C59]">
              Affirmations
            </p>
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-serif-display text-5xl md:text-7xl text-[#1A1A1A] max-w-4xl">
              A library of{" "}
              <em className="editorial-italic text-[#7B8871]">soft truths,</em>{" "}
              and a few written just for you.
            </h2>
            <p className="text-[#5C5C59] text-base leading-relaxed mt-8 max-w-2xl">
              Read curated affirmations to ground your day, or share what
              you're moving through and receive a small set written for this
              moment.
            </p>
            <Link
              to="/affirmations"
              data-testid="affirmations-teaser-cta"
              className="group inline-flex items-center gap-3 mt-10 border border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 font-eyebrow text-[11px] hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-500"
            >
              Enter the library
              <ArrowUpRight
                strokeWidth={1.5}
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* STYLING IMAGE STRIP */}
      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <img
              src={STYLING_IMAGE}
              alt="Stylish modern interior with minimalist chair"
              className="w-full h-[400px] md:h-[520px] object-cover"
            />
          </div>
          <div className="md:col-span-5">
            <p className="font-eyebrow text-[11px] text-[#5C5C59]">Style</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1A1A] mt-5">
              You are the{" "}
              <em className="editorial-italic text-[#7B8871]">first room</em>{" "}
              you enter every day.
            </h2>
            <p className="text-[#5C5C59] text-sm leading-relaxed mt-6">
              Through fashion psychology, we look at the way you dress as a
              language, and design a wardrobe that finally speaks fluently for
              you.
            </p>
            <Link
              to="/booking?service=fashion-psychology"
              data-testid="styling-cta"
              className="group inline-flex items-center gap-3 mt-8 font-eyebrow text-[11px] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1"
            >
              Book a styling session
              <ArrowRight
                strokeWidth={1.5}
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
